import {Suspense, useEffect} from 'react'
import {useFileTreeDiffSummaries} from '../../../../../page-data/loaders/use-file-tree-diff-summaries'
import {useInfiniteThreadsPageData} from '../../../../../page-data/loaders/use-threads'
import {usePullRequestBasePath, useThreads} from '../../../../../stores/layout-selectors'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useCompositeThreadPositioning} from '../../../../../hooks/use-composite-thread-positioning'

/**
 * This component is responsible for syncing comment data for the current pull request.
 * It fetches file tree diff summaries and threads data, ensuring that the latest
 * comments and diffs are available for proper thread positioning and rendering in the UI.
 *
 * After performing the data fetching, the hooks in the underlying components sync to the Zustand store
 * The `useThreads` Zustand selector is then used to retrieve threads data for rendering.
 *
 * `FileTreeDiffSummariesSync` is independent and runs immediately. `ThreadPositioningSync`
 * depends on the threads produced by `ThreadsPageDataSync`, so it is rendered by
 * `ThreadsPageDataSync` only once all thread pages have been fetched. This avoids the
 * redundant positioning requests that would otherwise fire on each intermediate page as
 * the store hydrates, letting positioning compute once over the complete thread set.
 *
 * Operates similarly to the `LazyDataLoader` component in `CommentsSidePanel`
 */
export function SyncCommentData() {
  return (
    <ErrorBoundary boundaryName="SyncCommentData" fallback={null}>
      <Suspense fallback={null}>
        <ThreadsPageDataSync />
        <FileTreeDiffSummariesSync />
      </Suspense>
    </ErrorBoundary>
  )
}

// Fetches all pages of threads data for the current pull request and hydrates the Zustand store with the latest threads.
// This is the primary data source for comment/thread rendering. The others enrich the rendering with additional context.
// Once every page has been fetched (`!hasNextPage`), it renders `ThreadPositioningSync` so positioning runs against the
// complete thread set rather than re-firing on each intermediate page.
function ThreadsPageDataSync() {
  const basePath = usePullRequestBasePath()

  const {hasNextPage, isFetchingNextPage, fetchNextPage} = useInfiniteThreadsPageData({
    pathName: basePath,
    hasMorePages: true,
  })

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  return hasNextPage ? null : <ThreadPositioningSync />
}

// Fetches file tree diff summaries for the current pull request and hydrates the Zustand store with the latest diff path order map.
// The diff path order map is consumed purely for sorting thread previews by their position in the diff (file order, then line number)
// via `sortThreadPreviewsByDiffOrder`; it has no effect on thread positioning, content, or which threads render.
// If we ever want to impose our own ordering instead of the diff order, this sync (and its fetch) can be dropped.
function FileTreeDiffSummariesSync() {
  const basePath = usePullRequestBasePath()
  useFileTreeDiffSummaries({basePath, enabled: true})
  return null
}

// Hydrates thread positioning for comments that fall outside the current diff, ensuring proper rendering and alignment in the UI.
// Rendered by `ThreadsPageDataSync` only after all thread pages have loaded, so it positions against the complete thread set.
function ThreadPositioningSync() {
  const threads = useThreads()
  useCompositeThreadPositioning(threads)
  return null
}
